import React from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import {
  ArrowRight,
  Clock,
  Linkedin,
  Mail,
  MapPin,
  MessageCircle,
  PhoneCall
} from "lucide-react";

const Section = styled.section`
  background: #ffffff;
  color: #0f172a;
  padding: 88px 0;

  @media (max-width: 760px) {
    padding: 64px 0;
  }
`;

const Inner = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 24px;
`;

const Panel = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 0.95fr) minmax(360px, 1.05fr);
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  overflow: hidden;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const Copy = styled(motion.div)`
  padding: 34px;
  background: #0f172a;
  color: #ffffff;

  @media (max-width: 560px) {
    padding: 24px;
  }

  .eyebrow {
    display: inline-flex;
    align-items: center;
    min-height: 32px;
    padding: 0 11px;
    border: 1px solid rgba(226, 232, 240, 0.2);
    border-radius: 8px;
    color: #bfdbfe;
    font-size: 0.76rem;
    font-weight: 850;
    text-transform: uppercase;
  }

  h2 {
    margin: 18px 0 14px;
    color: #ffffff;
    font-size: 3rem;
    line-height: 1.1;
    font-weight: 850;
    letter-spacing: 0;

    @media (max-width: 760px) {
      font-size: 2.1rem;
    }
  }

  p {
    max-width: 520px;
    color: #cbd5e1;
    font-size: 1rem;
    line-height: 1.75;
    margin: 0 0 28px;
  }
`;

const FitList = styled.div`
  display: grid;
  gap: 10px;

  div {
    display: flex;
    align-items: center;
    gap: 10px;
    color: #e2e8f0;
    font-size: 0.95rem;
  }

  .dot {
    width: 8px;
    height: 8px;
    border-radius: 999px;
    background: #22c55e;
    flex: 0 0 auto;
  }
`;

const ContactSide = styled(motion.div)`
  padding: 34px;
  background: #f8fafc;

  @media (max-width: 560px) {
    padding: 24px;
  }
`;

const ChannelGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 24px;

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

const Channel = styled.a`
  display: grid;
  grid-template-columns: 42px 1fr 18px;
  gap: 12px;
  align-items: center;
  min-height: 92px;
  padding: 16px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #ffffff;
  color: inherit;
  text-decoration: none;
  transition: transform 180ms ease, border-color 180ms ease;

  &:hover {
    transform: translateY(-2px);
    border-color: #bfdbfe;
  }

  .icon {
    width: 42px;
    height: 42px;
    border-radius: 8px;
    display: grid;
    place-items: center;
    color: #0f172a;
    background: #f1f5f9;
  }

  strong {
    display: block;
    color: #0f172a;
    font-size: 0.94rem;
    line-height: 1.25;
    letter-spacing: 0;
  }

  span {
    display: block;
    margin-top: 3px;
    color: #64748b;
    font-size: 0.84rem;
    line-height: 1.35;
    word-break: break-word;
  }
`;

const Meta = styled.div`
  display: grid;
  gap: 10px;
  padding: 18px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #ffffff;

  div {
    display: flex;
    align-items: center;
    gap: 10px;
    color: #475569;
    font-size: 0.92rem;
  }

  svg {
    color: #2563eb;
    flex: 0 0 auto;
  }
`;

const PHONE_DIGITS = "919322346482";
const WHATSAPP_URL = `https://wa.me/${PHONE_DIGITS}?text=${encodeURIComponent(
  "Hi Willovate team, I'd like to discuss a product or custom software project."
)}`;
const EMAIL_MAILTO = "mailto:admin@willovate.in?subject=Project%20Inquiry%20-%20Willovate";

const channels = [
  {
    icon: Mail,
    label: "Email",
    value: "admin@willovate.in",
    href: EMAIL_MAILTO
  },
  {
    icon: PhoneCall,
    label: "Call",
    value: "+91 93223 46482",
    href: "tel:+919322346482"
  },
  {
    icon: MessageCircle,
    label: "WhatsApp",
    value: "Fastest response",
    href: WHATSAPP_URL,
    external: true
  },
  {
    icon: Linkedin,
    label: "LinkedIn",
    value: "Company page",
    href: "https://www.linkedin.com/company/willovate/",
    external: true
  }
];

const Contact = () => {
  return (
    <Section>
      <Inner>
        <Panel>
          <Copy
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.35 }}
          >
            <span className="eyebrow">Start a conversation</span>
            <h2>Tell us what you need to manage better.</h2>
            <p>
              Share the workflow, team size, product interest, or custom build idea. We will help you decide whether an existing Willovate product fits or a custom platform makes more sense.
            </p>
            <FitList>
              <div><span className="dot" /> Product setup and onboarding</div>
              <div><span className="dot" /> Custom AI and dashboard builds</div>
              <div><span className="dot" /> WhatsApp marketing and automation workflows</div>
              <div><span className="dot" /> Long-term product support</div>
            </FitList>
          </Copy>

          <ContactSide
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.35, delay: 0.06 }}
          >
            <ChannelGrid>
              {channels.map((channel) => {
                const Icon = channel.icon;
                return (
                  <Channel
                    key={channel.label}
                    href={channel.href}
                    target={channel.external ? "_blank" : undefined}
                    rel={channel.external ? "noopener noreferrer" : undefined}
                  >
                    <span className="icon"><Icon size={19} /></span>
                    <div>
                      <strong>{channel.label}</strong>
                      <span>{channel.value}</span>
                    </div>
                    <ArrowRight size={16} />
                  </Channel>
                );
              })}
            </ChannelGrid>

            <Meta>
              <div>
                <MapPin size={17} />
                Nagpur, Maharashtra
              </div>
              <div>
                <Clock size={17} />
                Mon-Sat, 10:00-19:00 IST
              </div>
            </Meta>
          </ContactSide>
        </Panel>
      </Inner>
    </Section>
  );
};

export default Contact;
